import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Confirm } from '../components/Confirm';
import { Pagination } from '../components/Pagination';

/**
 * "SKU en Mercado Libre" (DECISIONES 2026-10-10). El SKU de la columna E de 02_CODIGOS se escribe como SKU de la publicación en
 * Mercado Libre (atributo SELLER_SKU), con su propio switch (apagado por defecto). Muestra las publicaciones que difieren
 * (SKU en ML hoy → SKU del ERP), lo que no se puede escribir (con qué hacer), el último resumen, los problemas y, si el ERP lo
 * apagó solo, por qué. Switch y "Enviar ahora" sólo admin; el resto lo ve. Con el switch apagado, "Enviar ahora" es una prueba
 * de pocas publicaciones. La lista se busca (SKU o MLA) y pagina en el servidor; "Enviar esta" (2026-10-12, sólo admin) manda
 * el SKU de una sola publicación por el mismo camino que "Enviar ahora". Si el ERP se apagó solo, la prueba de "Enviar ahora" o
 * "Enviar esta" pide confirmarla explicando por qué se apagó (2026-10-12).
 */

const URL = '/api/v3/ml/sku-ml';
const TITULO = 'SKU en Mercado Libre';
export const SWITCH = 'Escribir el SKU en Mercado Libre';
/** Confirmación de encender el switch (también la usa «Sincronizar con ML»). */
export const MENSAJE_ENCENDER_SKU_ML =
  'Desde la próxima sincronización de 02_CODIGOS (o el repaso de cada hora) el ERP va a escribir en Mercado Libre el SKU de la '
  + 'columna E en cada publicación que hoy tenga otro. Sólo cambia el SKU de la publicación. Antes probá con «Enviar ahora» '
  + '(con el switch apagado manda sólo unas pocas) y mirá en Mercado Libre 2 o 3 publicaciones, incluida una con variantes.';
/** Mientras corre, se vuelve a leer el estado cada tanto. */
const REFRESCO_MS = 4000;
const POR_PAGINA = 50;

/** Por qué no se puede: código del backend (el mensaje es sólo para mostrar). */
type CausaNoSePuede = 'sku_invalido' | 'validacion_ml' | 'cerrada' | 'variante_no_permitida' | 'ml_no_aplico' | 'otra';
interface UltimoIntento {
  resultado: 'ok' | 'error' | 'no_se_puede'; mensaje: string | null; causa?: CausaNoSePuede | null; skuEnviado: string; intentadoAt: string;
}
interface Diferencia {
  mlaId: string;
  variantId: string | null;
  titulo: string | null;
  variante: string | null;
  skuEnMl: string;
  skuErp: string;
  bloqueo: string | null;
  ultimoIntento: UltimoIntento | null;
}
interface Resumen {
  origen: 'manual' | 'prueba' | '02_CODIGOS' | 'periodico' | 'continuacion';
  usuario?: string | null;
  inicio: string;
  fin: string | null;
  revisadas: number;
  escritas: number;
  ok: number;
  yaEstaban: number;
  errores: number;
  noSePuede: number;
  detenida: string | null;
  pendientes: number;
}
interface Problema {
  mlaId: string; variantId: string; skuEnviado: string; resultado: 'error' | 'no_se_puede'; mensaje: string | null; causa?: CausaNoSePuede | null;
}
interface Estado {
  escrituraSkuMl: boolean;
  corriendo: boolean;
  aEnviar: number;
  noSePuede: number;
  sinLeer: number;
  revisarPlanilla: number;
  enOrden: number;
  tope: number;
  topePrueba: number;
  /** Diferencias que coinciden con la búsqueda (todas las páginas). */
  total: number;
  pagina: number;
  porPagina: number;
  /** La página pedida. */
  diferencias: Diferencia[];
  ultimaCorrida: Resumen | null;
  /** El ERP apagó el switch solo (cambio inesperado en una publicación): por qué, si se sabe. */
  apagadoSolo: { motivo: string; mlaId: string } | null;
  /** El ERP se apagó solo y nadie lo volvió a encender: la prueba se confirma (confirmarTrasApagado). */
  detenidoSolo?: boolean;
  problemas: Problema[];
}

const n = (v: number) => v.toLocaleString('es-AR');
const plural = (k: number, uno: string, varios: string) => `${n(k)} ${k === 1 ? uno : varios}`;
const ORIGEN: Record<Resumen['origen'], string> = {
  manual: 'Enviado a mano', prueba: 'Prueba', '02_CODIGOS': 'Después de 02_CODIGOS', periodico: 'Repaso de cada hora', continuacion: 'Continuación',
};
const RESULTADO: Record<UltimoIntento['resultado'], string> = { ok: 'bien', error: 'error', no_se_puede: 'no se puede' };
const nombrePublicacion = (d: { mlaId: string; variantId: string | null }) => `${d.mlaId}${d.variantId ? ` / variante ${d.variantId}` : ''}`;
const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '');

const ACCION_MISMO_PRODUCTO = 'En 02_CODIGOS, las publicaciones que Mercado Libre agrupa como el mismo producto tienen que llevar el mismo SKU en la columna E.';
/** Qué hacer con una "no se puede", según el código de causa del backend. */
function accionNoSePuede(causa: CausaNoSePuede | null | undefined): string {
  if (causa === 'sku_invalido') return 'Corregí la planilla (columna E de 02_CODIGOS).';
  if (causa === 'validacion_ml') return 'Corregí las características de la publicación en Mercado Libre o cargá el SKU a mano ahí.';
  if (causa === 'cerrada') return 'Publicación cerrada: no hace falta nada; si sigue en la planilla, sacala o reactivala en Mercado Libre.';
  return 'Mercado Libre no lo permite: cambiá el SKU a mano en la publicación de Mercado Libre.';
}
const textoIntento = (i: { resultado: UltimoIntento['resultado']; mensaje: string | null; causa?: CausaNoSePuede | null }) =>
  `${RESULTADO[i.resultado]} — ${i.mensaje ?? ''}${i.resultado === 'no_se_puede' ? `. ${accionNoSePuede(i.causa)}` : ''}`;
/** Por qué se apagó solo, para los avisos ("porque …" si se sabe). */
const porQueSeApago = (e: Estado | null) =>
  (e?.apagadoSolo ? `porque ${e.apagadoSolo.motivo}` : 'por un cambio inesperado en una publicación');

function textoResumen(r: Resumen): string {
  return [
    `${ORIGEN[r.origen]}${r.usuario ? ` de ${r.usuario}` : ''}: ${plural(r.revisadas, 'revisada', 'revisadas')}`,
    plural(r.escritas, 'escrita', 'escritas'),
    `${n(r.ok)} bien`,
    ...(r.yaEstaban > 0 ? [`${plural(r.yaEstaban, 'ya estaba bien', 'ya estaban bien')}`] : []),
    ...(r.errores > 0 ? [`${n(r.errores)} con error`] : []),
    ...(r.noSePuede > 0 ? [`${n(r.noSePuede)} no se puede`] : []),
    ...(r.pendientes > 0 ? [`${plural(r.pendientes, 'queda', 'quedan')} para la próxima`] : []),
  ].join(' · ');
}

export function SkuEnMercadoLibre({ esAdmin }: { esAdmin: boolean }) {
  const [estado, setEstado] = useState<Estado | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [confirmarSwitch, setConfirmarSwitch] = useState(false);
  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [filtro, setFiltro] = useState('');
  const [pagina, setPagina] = useState(1);
  /** "Enviar esta": la publicación a confirmar. */
  const [unaAEnviar, setUnaAEnviar] = useState<Diferencia | null>(null);

  /** Número del último pedido: una respuesta de un pedido anterior (refresco, cambio de página o búsqueda) se descarta. */
  const ultimoPedido = useRef(0);
  const cargar = useCallback(async () => {
    const pedido = ++ultimoPedido.current;
    setCargando(true);
    try {
      const params = new URLSearchParams({ ...(filtro ? { q: filtro } : {}), pagina: String(pagina), porPagina: String(POR_PAGINA) });
      const r = await api.get<Estado>(`${URL}?${params}`);
      if (pedido !== ultimoPedido.current) return;
      // La lista se achicó y la página quedó después de la última: se pide la última.
      const ultima = Math.max(1, Math.ceil(r.total / r.porPagina));
      if (r.total > 0 && pagina > ultima) { setPagina(ultima); return; }
      setEstado(r);
      setError(null);
    } catch (e) {
      if (pedido === ultimoPedido.current) setError(extractErrorMessage(e));
    } finally {
      if (pedido === ultimoPedido.current) setCargando(false);
    }
  }, [filtro, pagina]);

  const buscar = (e: FormEvent) => {
    e.preventDefault();
    setPagina(1);
    setFiltro(texto.trim());
  };

  useEffect(() => { void cargar(); }, [cargar]);

  const corriendo = estado?.corriendo === true;
  useEffect(() => {
    if (!corriendo) return undefined;
    const t = setInterval(() => { void cargar(); }, REFRESCO_MS);
    return () => clearInterval(t);
  }, [corriendo, cargar]);

  const guardarSwitch = async (valor: boolean) => {
    setConfirmarSwitch(false);
    setGuardando(true);
    try {
      const r = await api.post<{ escrituraSkuMl: boolean }>(`${URL}/switch`, { escrituraSkuMl: valor });
      setEstado((e) => (e ? { ...e, escrituraSkuMl: r.escrituraSkuMl === true } : e));
      setAviso(null);
    } catch (e) {
      setAviso(`No se pudo cambiar: ${extractErrorMessage(e)}`);
    } finally {
      setGuardando(false);
    }
  };

  const detenidoSolo = estado?.detenidoSolo === true;
  /** Con el ERP apagado solo, la prueba va confirmada (el diálogo explicó por qué se apagó). */
  const confirmacion = detenidoSolo ? { confirmarTrasApagado: true } : {};

  const enviar = async () => {
    setConfirmarEnvio(false);
    try {
      await api.post(`${URL}/enviar`, confirmacion);
      setEstado((e) => (e ? { ...e, corriendo: true } : e));
      setAviso(null);
    } catch (e) {
      setAviso(`No se pudo enviar: ${extractErrorMessage(e)}`);
      void cargar();
    }
  };

  const enviarUna = async () => {
    const d = unaAEnviar;
    setUnaAEnviar(null);
    if (!d) return;
    try {
      const r = await api.post<{ iniciada: boolean; mensaje?: string }>(`${URL}/enviar-una`, { mlaId: d.mlaId, variantId: d.variantId, ...confirmacion });
      if (r.iniciada) {
        setEstado((e) => (e ? { ...e, corriendo: true } : e));
        setAviso(null);
      } else {
        setAviso(`No se envió: ${r.mensaje ?? ''}`);
        void cargar();
      }
    } catch (e) {
      setAviso(`No se pudo enviar: ${extractErrorMessage(e)}`);
      void cargar();
    }
  };

  const encendido = estado?.escrituraSkuMl === true;
  const aEnviar = estado?.aEnviar ?? 0;
  const tope = estado?.tope ?? 100;
  const enPrueba = Math.min(aEnviar, estado?.topePrueba ?? 3);
  const r = estado?.ultimaCorrida ?? null;
  const diferencias = estado?.diferencias ?? [];
  const problemas = estado?.problemas ?? [];

  return (
    <section aria-label={TITULO} className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-slate-800">{TITULO}</h3>
          {estado && (
            <Badge tone={aEnviar > 0 ? 'amber' : 'green'}>{`${plural(aEnviar, 'publicación', 'publicaciones')} con otro SKU en Mercado Libre`}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => void cargar()} loading={cargando}>Actualizar</Button>
          {esAdmin && (
            <Button size="sm" onClick={() => setConfirmarEnvio(true)} disabled={!estado || corriendo || aEnviar === 0}>Enviar ahora</Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
      <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
        <input
          type="checkbox"
          checked={encendido}
          disabled={!esAdmin || !estado || guardando}
          onChange={(e) => (e.target.checked ? setConfirmarSwitch(true) : void guardarSwitch(false))}
        />
        {SWITCH}
      </label>
      {(estado?.apagadoSolo || detenidoSolo) && <Badge tone="red">se apagó solo</Badge>}
      </div>
      <p className="text-sm text-slate-600 max-w-4xl">
        Encendido, el SKU de la columna E de la planilla 02_CODIGOS (por ejemplo &quot;A-x1&quot;) pasa a ser el SKU de la publicación en
        Mercado Libre, el que se ve en las ventas. Se envía solo después de cada sincronización de la planilla que cambia publicaciones
        y una vez por hora, de a una publicación y hasta {n(tope)} por vez. Después de escribir, el ERP vuelve a leer la publicación para
        confirmar que quedó bien; si Mercado Libre cambió algo más, se apaga solo. No cambia stock, precio ni estado. Apagado, no se
        escribe nada salvo la prueba de «Enviar ahora» (hasta {n(estado?.topePrueba ?? 3)} publicaciones); esta lista muestra igual
        lo que se enviaría.
      </p>
      {!esAdmin && <p className="text-xs text-slate-500">Sólo un administrador puede cambiarlo o enviar.</p>}
      {estado && (estado.sinLeer > 0 || estado.revisarPlanilla > 0) && (
        <ul className="text-xs text-slate-600 space-y-0.5">
          {estado.sinLeer > 0 && (
            <li>{`${plural(estado.sinLeer, 'publicación todavía sin leer', 'publicaciones todavía sin leer')} de Mercado Libre: sincronizá las publicaciones (Base y publicaciones) para compararlas.`}</li>
          )}
          {estado.revisarPlanilla > 0 && (
            <li>{`${plural(estado.revisarPlanilla, 'publicación espera', 'publicaciones esperan')} que se corrija la columna E en 02_CODIGOS (el SKU se compone de sí mismo): no se envían.`}</li>
          )}
        </ul>
      )}

      {error && <p role="alert" className="text-sm text-red-600">No se pudo cargar: {error}</p>}
      {aviso && <p role="alert" className="text-sm text-red-600">{aviso}</p>}
      {corriendo && <p role="status" className="text-sm text-slate-700">Enviando el SKU a Mercado Libre, de a una publicación…</p>}
      {r && (
        <p className="text-sm text-slate-700">
          <span className="font-medium">{`Última corrida (${fecha(r.fin ?? r.inicio)}): `}</span>
          <span>{textoResumen(r)}</span>
          {r.detenida && <span className="block text-amber-700">{r.detenida}</span>}
        </p>
      )}

      <form onSubmit={buscar} className="flex flex-wrap items-end gap-2">
        <input
          type="search"
          aria-label="Buscar por SKU o publicación"
          placeholder="SKU o publicación (MLA)"
          className="w-64 rounded border border-slate-300 px-2 py-1 text-sm"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <Button type="submit" variant="secondary" size="sm">Buscar</Button>
      </form>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-1 px-2 font-medium">Publicación</th>
              <th className="py-1 px-2 font-medium">Variante</th>
              <th className="py-1 px-2 font-medium">SKU en ML hoy</th>
              <th className="py-1 px-2 font-medium">SKU del ERP</th>
              <th className="py-1 px-2 font-medium">Estado</th>
              {esAdmin && <th className="py-1 px-2 font-medium"><span className="sr-only">Enviar</span></th>}
            </tr>
          </thead>
          <tbody>
            {diferencias.length === 0 && (
              <tr>
                <td colSpan={esAdmin ? 6 : 5} className="py-3 px-2 text-slate-500">
                  {cargando && !estado ? 'Cargando…' : filtro ? 'Ninguna publicación con otro SKU coincide con la búsqueda' : 'Todas las publicaciones tienen el SKU del ERP'}
                </td>
              </tr>
            )}
            {diferencias.map((d) => (
              <tr key={`${d.mlaId}-${d.variantId ?? ''}`} className="border-b border-slate-100 align-top">
                <td className="py-1 px-2">
                  <div className="font-medium tabular-nums">{d.mlaId}</div>
                  {d.titulo && <div className="text-xs text-slate-500">{d.titulo}</div>}
                </td>
                <td className="py-1 px-2 text-xs">{d.variante ?? (d.variantId ? `Variante ${d.variantId}` : '—')}</td>
                <td className="py-1 px-2">{d.skuEnMl === '' ? <span className="text-slate-500">(sin SKU)</span> : d.skuEnMl}</td>
                <td className="py-1 px-2 font-medium">{d.skuErp}</td>
                <td className="py-1 px-2 text-xs">
                  {d.bloqueo
                    ? <span className="text-amber-700">{`No se puede en esta publicación: ${d.bloqueo}. ${ACCION_MISMO_PRODUCTO}`}</span>
                    : d.ultimoIntento && d.ultimoIntento.resultado !== 'ok'
                      ? <span className={d.ultimoIntento.resultado === 'error' ? 'text-red-700' : 'text-amber-700'}>
                        {`Último intento: ${textoIntento(d.ultimoIntento)}`}
                      </span>
                      : <span className="text-slate-600">Se envía</span>}
                </td>
                {esAdmin && (
                  <td className="py-1 px-2 text-right">
                    {!d.bloqueo && (
                      <Button variant="ghost" size="sm" onClick={() => setUnaAEnviar(d)} disabled={corriendo}>Enviar esta</Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {estado && estado.total > estado.porPagina && (
        <Pagination page={pagina} pageSize={estado.porPagina} total={estado.total} onChange={setPagina} />
      )}

      {problemas.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-700">{`Problemas de los últimos envíos (${n(problemas.length)})`}</summary>
          <ul className="mt-1 space-y-0.5 text-xs">
            {problemas.map((p) => (
              <li key={`${p.mlaId}-${p.variantId}`} className={p.resultado === 'error' ? 'text-red-700' : 'text-amber-700'}>
                {`${p.mlaId}${p.variantId ? ` / variante ${p.variantId}` : ''} (${p.skuEnviado}): ${textoIntento(p)}`}
              </li>
            ))}
          </ul>
        </details>
      )}

      <Confirm
        open={confirmarSwitch}
        title={SWITCH}
        message={MENSAJE_ENCENDER_SKU_ML}
        confirmLabel="Encender"
        onCancel={() => setConfirmarSwitch(false)}
        onConfirm={() => void guardarSwitch(true)}
      />
      <Confirm
        open={confirmarEnvio}
        title="Enviar el SKU a Mercado Libre"
        message={
          encendido
            ? `Se va a escribir el SKU de ${plural(aEnviar, 'publicación', 'publicaciones')} en Mercado Libre`
              + `${aEnviar > tope ? ` (ahora las primeras ${n(tope)}; el resto en las próximas corridas)` : ''}. `
              + 'Va de a una y tarda unos minutos. Las que dicen "No se puede" no se envían.'
            : `${detenidoSolo ? `«${SWITCH}» se apagó solo ${porQueSeApago(estado)}. ¿Probar igual? ` : ''}`
              + `Prueba: se va a escribir el SKU de hasta ${plural(enPrueba, 'publicación', 'publicaciones')} (una con variantes si hay) `
              + 'con el switch apagado. Después mirá esas publicaciones en Mercado Libre: que el SKU cambió y que no perdieron nada.'
        }
        confirmLabel={!encendido && detenidoSolo ? 'Probar igual' : 'Enviar'}
        onCancel={() => setConfirmarEnvio(false)}
        onConfirm={() => void enviar()}
      />
      <Confirm
        open={unaAEnviar !== null}
        title="Enviar el SKU de esta publicación"
        message={
          unaAEnviar
            ? `${!encendido && detenidoSolo ? `«${SWITCH}» se apagó solo ${porQueSeApago(estado)}. ¿Probar igual? ` : ''}`
              + `${encendido ? 'Se' : 'Prueba con el switch apagado: se'} va a escribir el SKU «${unaAEnviar.skuErp}» en ${nombrePublicacion(unaAEnviar)} `
              + 'en Mercado Libre. Después el ERP la vuelve a leer para confirmar que quedó bien.'
            : ''
        }
        confirmLabel={!encendido && detenidoSolo ? 'Probar igual' : 'Enviar'}
        onCancel={() => setUnaAEnviar(null)}
        onConfirm={() => void enviarUna()}
      />
    </section>
  );
}
